import { useGet } from './useFetch'
import { useAuth } from './useAuth'
import { UserRoleListResponse } from '@magnetar/admin-api-interfaces'
import { logger } from '@magnetar/utils'
import { buildRolesPath } from '@/services/rolesPath'

// UserRoleScopeType values (避免 Vite ESM 導入問題)
export type UserRoleScopeType = 'sys_admin' | 'provider' | 'clinic_group' | 'ad_supplier_group' | 'clinic' | 'ad_supplier'

interface UseUserRolesOptions {
  page?: number
  pageSize?: number
  search?: string
  scopeType?: UserRoleScopeType
  providerId?: number
  clinicId?: number
  supplierId?: number
  clinicGroupId?: number
  adSupplierGroupId?: number
}

export function useUserRoles(options: UseUserRolesOptions = {}) {
  const { token } = useAuth()
  const { page = 1, pageSize = 10, search = '', scopeType, providerId, clinicId, supplierId, clinicGroupId, adSupplierGroupId } =
    options

  // The parent id lives in the URL now (nested per-scope endpoints). System scope
  // (undefined / sys_admin) has no parent; every other scope needs its parent id
  // before we can build a valid path — until then, skip the fetch.
  const parentId =
    scopeType === 'provider'
      ? providerId
      : scopeType === 'clinic'
        ? clinicId
        : scopeType === 'ad_supplier'
          ? supplierId
          : scopeType === 'clinic_group'
            ? clinicGroupId
            : scopeType === 'ad_supplier_group'
              ? adSupplierGroupId
              : undefined
  const needsParent = !!scopeType && scopeType !== 'sys_admin'
  const ready = !!token && (!needsParent || parentId != null)

  const queryParams = new URLSearchParams()
  queryParams.set('page', page.toString())
  queryParams.set('pageSize', pageSize.toString())
  if (search) queryParams.set('search', search)

  const url = ready ? `${buildRolesPath(scopeType, parentId)}?${queryParams.toString()}` : null

  const { data, error, isLoading, mutate } = useGet<UserRoleListResponse>(
    url,
    undefined,
    token ?? '',
    undefined,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
    },
  )

  if (error) {
    logger.error('User roles fetch error:', error)
  }

  return {
    userRoles: data?.data?.items ?? [],
    pagination: data?.data
      ? {
          total: data.data.total,
          page: data.data.page,
          pageSize: data.data.pageSize,
          totalPages: data.data.totalPages,
        }
      : null,
    isLoading,
    isError: error,
    mutate,
  }
}
